import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { AttachmentFileList } from 'owa-attachment-file-types';
import {
    FilePickerAction,
    FileProvidersFilter,
    FileSelectionFilter,
    IAttachmentLocalComputerFilePicker,
    lazyAttachmentFilePicker,
    CreateAttachmentHandlerParams,
} from 'owa-attachment-filepicker';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import {
    AttachmentSuggestionsMenuItems,
    lazyCreateCloudFileFromAttachmentData,
    ReplyWithAttachmentSuggestion,
} from 'owa-attachment-suggestions';
import {
    CreateAttachmentsProperties,
    lazyGetAvailableLocalAttachmentSize,
} from 'owa-attachment-well-data';
import { lazyInsertLinks } from 'owa-insert-link';
import getCurrentFromAddress from 'owa-mail-compose-actions/lib/utils/getFromAddressFromRecipientWell';
import { ComposeViewState } from 'owa-mail-compose-store';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import { AttachmentMultiMenuMap } from '../types/AttachmentMultiMenuMap';
import { getAttachmentMenuItemsBasedOnClickHandler } from './getAttachmentMenuItemsBasedOnClickHandler';
import { createReplyWithContextualMenuItemSeparator } from './attachButtonMenuItemBuilder';
import { logAttachOrInsertMenuOpened } from './logAttachOrInsertMenuOpened';
import { logAttachOrInsertMenuDismissed } from './logAttachOrInsertMenuDismissed';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import { getInsertLinkButtonsList } from '../utils/getAttachmentCommandItems';

export function getInsertLinkMenuItems(
    composeViewState: ComposeViewState,
    sxsId: string,
    targetWindow: Window,
    getAttachmentLocalFileToLinkPicker: () => IAttachmentLocalComputerFilePicker | null,
    getAttachmentSuggestionsMenuItems: () => AttachmentSuggestionsMenuItems | null,
    shouldPlaceSuggestionsInBottom: boolean
): { menuItems: IContextualMenuItem[]; onMenuOpened: () => void; onMenuDismissed: () => void } {
    const attachmentPolicy = getAttachmentPolicy();
    if (
        !attachmentPolicy.ReferenceAttachmentsEnabled ||
        !attachmentPolicy.AttachmentDataProviderAvailable
    ) {
        return { menuItems: [], onMenuOpened: null, onMenuDismissed: null };
    }

    const openFilePickerForInsertLinks = async () => {
        const maximumClassicAttachmentSize = (await lazyGetAvailableLocalAttachmentSize.import())(
            composeViewState.attachmentWell
        );
        lazyAttachmentFilePicker.importAndExecute(
            (createAttachmentHandlerParams: CreateAttachmentHandlerParams) => {
                const baseProperties = {
                    isInline: createAttachmentHandlerParams.isInline,
                    isHiddenInline: false,
                };
                const properties: CreateAttachmentsProperties = {
                    ...baseProperties,
                    shouldShare: false,
                };
                aFPCreateAttachmentHandler(
                    createAttachmentHandlerParams.files,
                    composeViewState,
                    properties,
                    createAttachmentHandlerParams.dataProviderType
                );
            },
            async params => {
                const insertLinks = await lazyInsertLinks.import();
                insertLinks(
                    params.files,
                    composeViewState,
                    composeViewState,
                    getCurrentFromAddress(composeViewState.toRecipientWell),
                    targetWindow,
                    sxsId,
                    null /*selectionText*/
                );
            },
            {
                fileSelectionFilter: FileSelectionFilter.None,
                fileProvidersFilter: FileProvidersFilter.None,
                maxAllowedLocalFilesSize: maximumClassicAttachmentSize,
                supportsInsertLink: true,
                forcedAction: FilePickerAction.InsertAsLink,
                targetWindow: targetWindow,
            }
        );
    };

    const attachmentMultiMenuEntriesClickHandler: AttachmentMultiMenuMap<() => void> = {
        computer: null,
        onlineLocation: openFilePickerForInsertLinks,
        uploadAndShareFromCloud: attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()
            ? () => {
                  const attachmentLocalFileToLinkPicker = getAttachmentLocalFileToLinkPicker();
                  attachmentLocalFileToLinkPicker?.click();
              }
            : null,
    };
    let menuItems: IContextualMenuItem[] = getAttachmentMenuItemsBasedOnClickHandler(
        attachmentMultiMenuEntriesClickHandler
    );

    // Attachment suggestions
    const attachmentSuggestionsMenuItems = getAttachmentSuggestionsMenuItems();
    const attachmentSuggestions: IContextualMenuItem[] = attachmentSuggestionsMenuItems?.getContextualMenuItems(
        true /* shouldUseSuggestions */,
        composeViewState.referenceItemId,
        true /* referenceAttachmentsSupported */,
        false /* classicAttachmentsSupported */,
        true /* shouldUseSuggetedFilesTitle */,
        async (suggestion: ReplyWithAttachmentSuggestion, suggestionIndex: number) => {
            const createCloudFileFromAttachmentData = await lazyCreateCloudFileFromAttachmentData.import();
            const insertLinks = await lazyInsertLinks.import();
            await createCloudFileFromAttachmentData(suggestion, cloudFile => {
                const files: AttachmentFileList = [cloudFile];
                insertLinks(
                    files,
                    composeViewState,
                    composeViewState,
                    getCurrentFromAddress(composeViewState.toRecipientWell),
                    targetWindow,
                    sxsId,
                    null /*selectionText*/
                );
            });
        }
    );
    if (attachmentSuggestions) {
        if (shouldPlaceSuggestionsInBottom) {
            menuItems.push(...attachmentSuggestions);
        } else {
            menuItems = attachmentSuggestions
                .concat([createReplyWithContextualMenuItemSeparator()])
                .concat(menuItems);
        }
    }

    const onMenuOpened = () =>
        logAttachOrInsertMenuOpened(
            composeViewState,
            getAttachmentSuggestionsMenuItems,
            getInsertLinkButtonsList().join(','),
            true /* referenceAttachmentsSupported */,
            false /* classicAttachmentsSupported */
        );
    const onMenuDismissed = () =>
        logAttachOrInsertMenuDismissed(
            composeViewState,
            getAttachmentSuggestionsMenuItems,
            true /* referenceAttachmentsSupported */,
            false /* classicAttachmentsSupported */
        );
    return { menuItems, onMenuOpened, onMenuDismissed };
}
